/* eslint-disable @typescript-eslint/no-explicit-any */
import i18n from 'i18next';
import { CHANGE_BEHAVIOR_OPTION, TABLE_CELL_TYPE, VIEW_MODE } from 'types/enums';
import type { TFunction } from 'i18next';
import type {
  AgendaAttributeValue,
  AgendaRecordItem,
  AttributeType,
  CalculateResponseAttribute,
  DataSchema,
  DisplayValue,
  FormItemDefinition,
  FlattenedJoinedAgendaRecord,
  FormItemType,
  FormItemWithoutVisibilities,
  GetAgendaRecordsRes,
  GetValueFormItems,
  GetViewColumn,
  InlineRecordType,
  RecordAttribute,
  RecordFormItem,
  RecordTableItem,
  RowsMap,
  TablesViewParam,
  TabSection,
  ViewParameter,
  RecordsForViewLightRes,
} from 'types';
import type { FieldValues, UseFormWatch } from 'react-hook-form';
import { formatDateForBE, formatDateTimeForInputs } from '../dateAndTime';
import { toLowerCamelCase } from '../string';
import { isObjectArray, isStringArray, isValueObjectArray } from '../typeChecks';
import type { LocalFormItem, LocalSection } from 'types/agendaForm';
import { normalizeToArray } from '../common';
import type { FormItem, TablesFormItem, UserItem } from 'types/formItem';
import type { NestedListData } from 'types/records';
import { getFormFieldPath } from 'components/common/Table/editableComponentFactory';
import type { RegisteredForm, RegisteredFormData } from 'contexts/FormRegistryProvider';
import type { TableDirtyFields, TableFormData } from 'components/common/Table/Table';
import { getStartPos } from 'utils/section/gridUtils';
import type { VisibilityResultWithName } from 'utils/validations/multipleDefinitions';
import type { RecordDisplayAttribute, RecordsDisplayAttribute } from 'types/recordAttribute';
import { formatDisplayValue } from './formatDisplayValue';

export const NEW_RECORD_PREFIX = 'new-';

export const initialAttributeValues = {
  automaticSequenceAttributeInput: null,
  buttonAttributeInput: null,
  checkboxAttributeInput: null,
  commentAttributeInput: null,
  computedAttributeInput: null,
  dateAttributeInput: null,
  emailAttributeInput: null,
  fileUploadAttributeInput: null,
  numberAttributeInput: null,
  phoneNumberAttributeInput: null,
  radioButtonAttributeInput: null,
  recordsAttributeInput: null,
  richTextAttributeInput: null,
  tablesAttributeInput: null,
  textAttributeInput: null,
  usersAttributeInput: null,
};

export const getCellTypeByFormType = (formType: string) => {
  switch (formType) {
    case 'Text':
    case 'Number':
    case 'PhoneNumber':
    case 'AutomaticSequence':
    case 'RichText':
    case 'RadioButton':
    case 'Computed':
      return TABLE_CELL_TYPE.TEXT;
    case 'Email':
      return TABLE_CELL_TYPE.EMAIL;
    case 'Date':
      return TABLE_CELL_TYPE.DATE;
    case 'Users':
      return TABLE_CELL_TYPE.USERS;
    case 'Tables':
    case 'Records':
      return TABLE_CELL_TYPE.NAME_ARRAY;
    case 'FileUpload':
      return TABLE_CELL_TYPE.FILE_UPLOAD;
    case 'Checkbox':
      return TABLE_CELL_TYPE.CHECKBOX;
    case 'Comment':
      return TABLE_CELL_TYPE.COMMENT;
    //Helios
    case 'Value':
    case 'CalendarName':
    case 'CalendarCategory':
    case 'CalendarDateFrom':
    case 'CalendarDateTo':
    case 'CalendarAllDay':
      return TABLE_CELL_TYPE.HELIOS_VALUE;
    case 'StaticSelection':
      return TABLE_CELL_TYPE.HELIOS_STATIC_SELECTION;
    case 'Browse':
      return TABLE_CELL_TYPE.HELIOS_BROWSE;
    case 'File':
      return TABLE_CELL_TYPE.HELIOS_FILE;
    case 'Link':
      return TABLE_CELL_TYPE.HELIOS_LINK;
    case 'Button':
      return TABLE_CELL_TYPE.HELIOS_BUTTON;
  }
};

export const normalizeUserIds = (value: unknown): string[] =>
  normalizeToArray<string | UserItem | null>(value)
    .map((user) => (typeof user === 'string' ? user : user?.userId))
    .filter((id): id is string => Boolean(id));

export const normalizeComments = (value: unknown): { author: string; createdAt: string; text: string }[] =>
  normalizeToArray<{ author?: string | UserItem | null; createdAt?: string; text?: string } | null>(value)
    .map((comment) => ({
      author: typeof comment?.author === 'string' ? comment.author : comment?.author?.userId,
      createdAt: comment?.createdAt,
      text: comment?.text ?? '',
    }))
    .filter((comment): comment is { author: string; createdAt: string; text: string } => Boolean(comment?.author && comment?.createdAt));

export const getAttributeValue = (type: string, value: AgendaAttributeValue, keepNewValues?: boolean) => {
  switch (type) {
    case 'Text':
    case 'PhoneNumber':
    case 'Email':
    case 'RadioButton':
      return {
        value,
      };
    case 'Number':
      return {
        number: Number(value),
      };
    case 'Checkbox':
      return {
        value: value === 'false' ? false : Boolean(value),
      };
    case 'RichText':
      const strippedHtml = (value as string)?.replace(/<[^>]+>/g, '');
      return {
        plainText: strippedHtml,
        richText: value,
      };
    case 'Button':
      return {
        isClicked: value ?? false,
      };
    case 'FileUpload':
      return {
        values: (value as unknown as { fileId: string }[]).map(({ fileId }) => fileId),
      };
    case 'Comment':
      return {
        comments: normalizeComments(value),
      };
    case 'Date':
      return {
        date: formatDateForBE(value as string),
      };
    case 'Computed':
      return {
        value,
      };
    case 'AutomaticSequence':
      return {
        value: null,
      };
    case 'Users':
      return {
        users: normalizeUserIds(value),
      };
    case 'Tables':
      return {
        rows: isObjectArray(value) ? value.map(({ id }) => id).filter(Boolean) : value,
      };
    case 'Records':
      const raw = isObjectArray(value) ? value.map((v) => v.id).filter(Boolean) : value;

      if (!isStringArray(raw)) return { records: raw };
      if (keepNewValues) return { records: normalizeRecordIds(raw) };

      return { records: raw.filter((r) => !r.startsWith(NEW_RECORD_PREFIX)) };
  }
};

export const getAttributeForSave = (
  type: FormItemType,
  formItemId: string,
  value: AgendaAttributeValue,
  definition?: FormItemDefinition
): {
  formItemId: string;
  [key: string]: any;
} & typeof initialAttributeValues => {
  const attributeName = `${toLowerCamelCase(type)}AttributeInput`;
  const payloadValue = definition ? getPortalSafeValue(definition, value) : value;
  if (type === 'Checkbox') {
    return {
      formItemId,
      ...initialAttributeValues,
      [attributeName]: payloadValue ? getAttributeValue(type, payloadValue, false) : { value: false },
    };
  }
  return {
    formItemId,
    ...initialAttributeValues,
    [attributeName]: payloadValue ? getAttributeValue(type, payloadValue, false) : null,
  };
};

export const hasRecordsAttributeInput = (attribute: ReturnType<typeof getAttributeForSave>): boolean => {
  if (attribute.recordsAttributeInput === null) return false;
  if (typeof attribute.recordsAttributeInput !== 'object') return false;
  if (!('records' in attribute.recordsAttributeInput)) return false;

  const recordsInput: Record<string, unknown> = attribute.recordsAttributeInput;
  return Array.isArray(recordsInput.records);
};

export const getRecordsFromAttribute = (attribute: ReturnType<typeof getAttributeForSave>): string[] | null => {
  if (!hasRecordsAttributeInput(attribute)) return null;

  const recordsInputRaw = attribute.recordsAttributeInput;
  if (!recordsInputRaw || typeof recordsInputRaw !== 'object') return null;

  const recordsInput: Record<string, unknown> = recordsInputRaw;
  const records = recordsInput.records;

  if (!Array.isArray(records)) return null;
  return records;
};

export const setRecordsOnAttribute = (attribute: ReturnType<typeof getAttributeForSave>, records: string[]): void => {
  if (!hasRecordsAttributeInput(attribute)) return;

  const recordsInputRaw = attribute.recordsAttributeInput;
  if (!recordsInputRaw || typeof recordsInputRaw !== 'object') return;

  const recordsInput: Record<string, unknown> = recordsInputRaw;
  recordsInput.records = records;
};

export const formValueChanged = (type: FormItemType | unknown, formValue: unknown, rowValue: unknown) => {
  const fValue = formValue === '' ? undefined : formValue;

  switch (type) {
    case 'Date':
      return fValue !== (rowValue as string[])?.[0];
    case 'Checkbox':
      return convertValueByType(fValue, TABLE_CELL_TYPE.CHECKBOX) !== Boolean(rowValue);
    case 'FileUpload':
      return JSON.stringify(fValue) !== JSON.stringify(convertValueByType(rowValue, TABLE_CELL_TYPE.FILE_UPLOAD));
    case 'Tables':
      return JSON.stringify(fValue) !== JSON.stringify(convertValueByType(rowValue, TABLE_CELL_TYPE.NAME_ARRAY));
    case 'Users':
      const users = ((rowValue as []) ?? []).map((user: UserItem) => user.userId);
      return JSON.stringify(formValue) !== JSON.stringify(users);
    case 'Records':
      return JSON.stringify(formValue ?? []) !== JSON.stringify(rowValue ?? []);
    case 'Number':
      return Number(fValue) !== Number(rowValue);
    default:
      return JSON.stringify(fValue) !== JSON.stringify(rowValue);
  }
};

export const convertValueByType = (value: unknown, type: TABLE_CELL_TYPE): string | number | boolean | object | unknown[] => {
  switch (type) {
    case TABLE_CELL_TYPE.NUMBER:
      return Number(value);
    case TABLE_CELL_TYPE.CHECKBOX:
      return value === 'false' ? false : Boolean(value);
    case TABLE_CELL_TYPE.USERS:
    case TABLE_CELL_TYPE.NAME_ARRAY:
      return normalizeToArray(value);
    case TABLE_CELL_TYPE.COMMENT:
      return value as unknown[];
    case TABLE_CELL_TYPE.FILE_UPLOAD:
      const normalizedFiles = normalizeToArray<string>(value);
      return normalizedFiles.map((fileId: string) => ({ fileId }));
    case TABLE_CELL_TYPE.HELIOS_BROWSE:
      return value as object;
    default:
      return String(value ?? '');
  }
};

export const getAttributeForCalculate = (type: FormItemType, formItemId: string, value: AgendaAttributeValue, definition?: FormItemDefinition) => {
  const attributeName = `${toLowerCamelCase(type)}AttributeInput`;
  const payloadValue = definition ? getPortalSafeValue(definition, value) : value;
  if (type === 'Checkbox') {
    return {
      formItemId,
      [attributeName]: payloadValue ? getAttributeValue(type, payloadValue, true) : { value: false },
    };
  }

  if (payloadValue === null || payloadValue === undefined) {
    return null;
  }

  return {
    formItemId,
    [attributeName]: getAttributeValue(type, payloadValue, true),
  };
};

export const fixAttributeRecordIds = (attributes: InlineRecordType[] = []): InlineRecordType[] =>
  attributes.map(({ recordId, ...rest }) => ({
    ...rest,
    recordId: normalizeRecordId(recordId),
  }));

export const setAttributeValue = (
  type: string,
  attribute?: RecordAttribute | RecordDisplayAttribute | null,
  getValue?: boolean,
  definition?: FormItemDefinition
) => {
  const attrKey = attribute?.[`${toLowerCamelCase(type)}Attribute` as keyof typeof attribute] as any; //TODO
  switch (type) {
    case 'Text':
    case 'PhoneNumber':
    case 'Email':
    case 'RadioButton':
    case 'Number':
    case 'AutomaticSequence':
      return attrKey?.value ?? '';
    case 'Computed':
      return formatComputedValue(attrKey?.value, i18n.language) ?? '';
    case 'Checkbox':
      const attrDefinition = definition?.[toLowerCamelCase(type) as keyof FormItemDefinition];
      const checkedByDefault = (attrDefinition as FormItemDefinition['Checkbox'])?.isCheckedByDefault ?? false;

      return attrKey?.value ?? checkedByDefault;
    case 'RichText':
      return attrKey?.richText;
    case 'Button':
      return attrKey?.isClicked;
    case 'FileUpload':
      return (
        attrKey?.values.map((value: string) => {
          if (getValue) {
            return value;
          } else {
            return {
              fileId: value,
            };
          }
        }) ?? []
      );
    case 'Comment':
      return attrKey?.comments;
    case 'Date':
      return formatDateTimeForInputs(attrKey?.date);
    case 'Users':
      return (
        attrKey?.users.map(({ userId, fullName }: UserItem) => {
          if (getValue) {
            return fullName ?? 'notFilled';
          } else {
            return userId;
          }
        }) ?? []
      );
    case 'Tables':
      return attrKey?.rows ?? [];
    case 'Records':
      return attrKey?.records ?? [];
    case 'RecordsExternal':
      return attrKey?.records ?? [];
  }
};

export const getFormItemType = (data: GetAgendaRecordsRes<AgendaRecordItem[]>, id: string) => {
  if (!data || !data.items) {
    return '';
  }

  const attributes = data.items.flatMap((item) => item.attributes);
  const formItem = attributes.find((item) => item.formItem?.id === id);

  return formItem?.formItem?.definition.type;
};

export const getFiltersForBE = (data: GetAgendaRecordsRes<AgendaRecordItem[]>, filters: Record<string, string>) =>
  Object.keys(filters ?? []).reduce<
    {
      formItemId: string;
      formItemType: string;
      value: string;
    }[]
  >((acc, key) => {
    const type = getFormItemType(data, key);
    if (type && filters[key]) {
      acc.push({
        formItemId: key,
        formItemType: type,
        value: filters[key],
      });
    }
    return acc;
  }, []);

export const getRecordViewParams = (viewParameters: ViewParameter[]): GetValueFormItems[] => {
  return (
    viewParameters.map(({ viewParameterId, parameterId, staticValue }) => ({
      viewFormItemId: viewParameterId ?? '',
      valueSourceType: 'FormItem',
      valueFormItemId: parameterId,
      valueDirect: staticValue ? [...staticValue] : null,
    })) ?? []
  );
};

export const getValuesByFormItemId = (items: AgendaRecordItem[], formItemIdToShow: string, getValue?: boolean): (string | DisplayValue)[] => {
  const values = items.reduce<(string | DisplayValue)[]>((acc, { attributes, id: itemId }) => {
    const item = attributes.find((attribute) => attribute?.formItem?.id === formItemIdToShow);
    if (!item) {
      if (getValue) {
        acc.push({ id: itemId, value: '' });
      }
      return acc;
    }
    const { id, state, formItem: itemFormItem, ...rest } = item ?? {};

    const newItem = { formItem: itemFormItem, attribute: rest } as {
      formItem: FormItemWithoutVisibilities;
      attribute: RecordAttribute;
    };

    const type = newItem?.formItem?.definition.type ?? '';
    const attribute = newItem.attribute as RecordAttribute;
    acc.push(
      getValue
        ? {
            id: itemId,
            value: setAttributeValue(type, attribute, true) ?? '',
          }
        : setAttributeValue(type, attribute)
    );
    return acc;
  }, []);

  return values;
};

export const getTableViewParams = (
  viewColumns: GetViewColumn[]
): {
  viewColumnId: string;
  valueColumnId: string;
  valueDirect: string | string[] | null;
  valueSourceType: string | null;
}[] =>
  viewColumns.map(({ columnId, viewColumnId }) => ({
    viewColumnId,
    valueColumnId: columnId,
    valueDirect: null,
    valueSourceType: null,
  }));

export const getTableParamsBySourceType = (viewColumns: GetViewColumn[], watch: UseFormWatch<FieldValues>, params: TablesViewParam[]) => {
  const tableViewParams = getTableViewParams(viewColumns);

  tableViewParams.forEach((viewColumn) => {
    const param = params.find((p) => p.columnId === viewColumn.valueColumnId);
    if (param) {
      switch (param.valueSourceType) {
        case 'UserId':
        case 'UserRole':
        case 'UserEmail':
        case 'CreatedDate':
        case 'UpdatedDate':
        case 'DateNow':
          viewColumn.valueSourceType = param.valueSourceType;
          break;
        case null:
          viewColumn.valueDirect = param.valueDirect;
          break;
        case 'FormItem':
          viewColumn.valueSourceType = 'FormItem';
          viewColumn.valueDirect = param.valueFormItemId
            ? (() => {
                const items = Array.isArray(watch(param.valueFormItemId))
                  ? watch(param.valueFormItemId)
                  : [watch(param.valueFormItemId)].filter(Boolean);
                const filteredValues = isValueObjectArray(items)
                  ? items.filter(({ value }) => value !== null && value !== undefined).map(({ value }) => value)
                  : items;

                return filteredValues.length > 0 ? filteredValues : null;
              })()
            : null;
          break;
      }
    }
  });

  return tableViewParams;
};

export const getChangeBehaviorOptions = (t: TFunction) => [
  { id: CHANGE_BEHAVIOR_OPTION.NONE, name: t('flow.modulesAndAgendas.noticeOnSaving.none') },
  { id: CHANGE_BEHAVIOR_OPTION.NOTIFY_USER_TO_SAVE, name: t('flow.modulesAndAgendas.noticeOnSaving.pointOut') },
  { id: CHANGE_BEHAVIOR_OPTION.SAVE_REQUIRED, name: t('flow.modulesAndAgendas.noticeOnSaving.forcedSave') },
  { id: CHANGE_BEHAVIOR_OPTION.CALCULATE, name: t('flow.modulesAndAgendas.noticeOnSaving.calculate') },
];

export const getChangeBehaviorOptionsButton = (t: TFunction) => [
  { id: CHANGE_BEHAVIOR_OPTION.NONE, name: t('flow.modulesAndAgendas.noticeOnSaving.none') },
  {
    id: CHANGE_BEHAVIOR_OPTION.NOTIFY_USER_TO_SAVE,
    name: t('flow.modulesAndAgendas.noticeOnSaving.pointOut'),
  },
  { id: CHANGE_BEHAVIOR_OPTION.SAVE_REQUIRED, name: t('flow.modulesAndAgendas.noticeOnSaving.forcedSave') },
  { id: CHANGE_BEHAVIOR_OPTION.CALCULATE, name: t('flow.modulesAndAgendas.noticeOnSaving.calculate') },
  {
    id: CHANGE_BEHAVIOR_OPTION.RUN_ACTIONS_WITHOUT_SAVING,
    name: t('flow.modulesAndAgendas.noticeOnSaving.runActionsWithoutSaving'),
  },
  {
    id: CHANGE_BEHAVIOR_OPTION.SAVE_BUT_DO_NOT_RUN_ACTIONS,
    name: t('flow.modulesAndAgendas.noticeOnSaving.saveButDoNotRunActions'),
  },
];

export const gridSort = (items: RecordFormItem[]): RecordFormItem[] =>
  items.toSorted((a, b) => {
    const startY_A = getStartPos(a.formItem.y);
    const startY_B = getStartPos(b.formItem.y);

    if (startY_A === startY_B) {
      const startX_A = getStartPos(a.formItem.x);
      const startX_B = getStartPos(b.formItem.x);

      return startX_A - startX_B;
    }

    return startY_A - startY_B;
  });

export const areSectionsEqual = (sections1: LocalSection[], sections2: LocalSection[]): boolean => {
  if (sections1.length !== sections2.length) {
    return false;
  }

  for (let i = 0; i < sections1.length; i++) {
    const section1 = sections1[i];
    const section2 = sections2[i];

    if (
      section1.name !== section2.name ||
      section1.gridSizeX !== section2.gridSizeX ||
      section1.gridSizeY !== section2.gridSizeY ||
      section1.id !== section2.id ||
      section1.description !== section2.description
    ) {
      return false;
    }

    if (!areItemsEqual(section1.items, section2.items)) {
      return false;
    }
  }

  return true;
};

const areItemsEqual = (items1: LocalFormItem[], items2: LocalFormItem[]): boolean => {
  if (items1.length !== items2.length) {
    return false;
  }

  for (let i = 0; i < items1.length; i++) {
    const item1 = items1[i];
    const item2 = items2[i];

    if (
      item1.x !== item2.x ||
      item1.y !== item2.y ||
      item1.identifier !== item2.identifier ||
      item1.isEmpty !== item2.isEmpty ||
      item1.data !== item2.data
    ) {
      return false;
    }
  }

  return true;
};

export const formatComputedValue = (value: unknown, locale: string): string => {
  if (typeof value !== 'string') {
    return String(value);
  }

  const parts = value.split(' ');
  const formattedParts = parts.map((part) => {
    const num = Number(part);
    if (isFinite(num) && part !== '') {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    }
    return part;
  });

  return formattedParts.join(' ');
};

export type TransformedDataType = { id: string; identifier: string; [key: string]: unknown };

const getRecordsDisplayValue = (
  attribute: RecordDisplayAttribute | null,
  formItem: RecordsDisplayAttribute['displayFormItem'] | undefined,
  recordId: string
) => {
  if (!attribute || !formItem) return recordId;

  try {
    const typedAttribute = attribute[`${toLowerCamelCase(formItem.type)}Attribute` as keyof RecordDisplayAttribute];
    if (
      !typedAttribute ||
      !Object.values(typedAttribute).some((value) => typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value)))
    )
      return recordId;

    const value = formItem.type === 'RichText' ? attribute.richTextAttribute?.plainText : setAttributeValue(formItem.type, attribute, true);
    if ((typeof value !== 'string' && typeof value !== 'number') || (typeof value === 'number' && !Number.isFinite(value))) return recordId;
    if (typeof value === 'string' && !value) return recordId;

    return (
      formatDisplayValue(formItem.type === 'Date' ? [value, formItem.dateMask ?? 'Year, Month, Day, Time'] : value, false, i18n.language) ?? recordId
    );
  } catch {
    return recordId;
  }
};

const getRecordsDisplayValues = (display: RecordsDisplayAttribute | null | undefined, recordIds: string[]) => {
  if (!Array.isArray(display?.values)) return [...recordIds];

  const validValues = display.values.filter((value) => typeof value?.recordId === 'string' && value.recordId.length > 0);
  const values = validValues.map(({ recordId, attribute }) => ({
    id: recordId,
    value: getRecordsDisplayValue(attribute, display.displayFormItem, recordId),
  }));

  if (validValues.length !== display.values.length) {
    const includedIds = new Set(values.map(({ id }) => id));
    for (const recordId of recordIds) {
      if (!includedIds.has(recordId)) {
        values.push({ id: recordId, value: recordId });
        includedIds.add(recordId);
      }
    }
  }

  return values;
};

const skipAttributes = ['visibilityResult', 'formItem', 'recordsDisplayAttribute'];
export const transformDataForTable = (items?: AgendaRecordItem[], overrideData?: InlineRecordType[]): TransformedDataType[] =>
  (items ?? []).map((item: AgendaRecordItem): TransformedDataType => {
    const override = overrideData?.find((overrideItem) => overrideItem.recordId === item.id);
    const result: Record<string, string | number | null | string[] | { id: string; value: string }[]> = {};

    // Create a map of formItemId -> override value for quick lookup
    const overrideMap = new Map<string, any>();
    if (override) {
      override.attributes.forEach((attr) => {
        // Find the attribute value from the override (skip formItemId and null values)
        const overrideValue = Object.entries(attr).find(([key, value]) => key !== 'formItemId' && value !== null && value !== undefined);

        if (overrideValue && attr.formItemId) {
          const [, attributeValue] = overrideValue;
          // Extract the actual value from the attribute structure
          if (attributeValue && typeof attributeValue === 'object') {
            const valueKey = Object.keys(attributeValue)[0];
            if (valueKey) {
              overrideMap.set(attr.formItemId, attributeValue[valueKey]);
            }
          }
        }
      });
    }

    const hasRowsValues = (attrValue: unknown): attrValue is { rowsValues: RecordTableItem[] } => {
      return typeof attrValue === 'object' && attrValue !== null && 'rowsValues' in attrValue;
    };

    item.attributes.forEach((attribute) => {
      const formItemId = attribute.formItem?.id;
      if (formItemId && attribute.formItem?.definition.type === 'Records') {
        const overrideValue = overrideMap.get(formItemId);
        result[formItemId] =
          overrideValue !== undefined
            ? overrideValue
            : getRecordsDisplayValues(attribute.recordsDisplayAttribute, attribute.recordsAttribute?.records ?? []);
        return;
      }
      (Object.keys(attribute) as (keyof typeof attribute)[]).forEach((key) => {
        const attrValue = attribute[key];

        if (attrValue && typeof attrValue === 'object' && !skipAttributes.includes(key) && attribute.formItem?.id) {
          // Check if we have an override value for this formItem
          const overrideValue = overrideMap.get(attribute.formItem.id);

          if (overrideValue !== undefined) {
            // Use override value instead of database value
            result[attribute.formItem.id] = overrideValue;
          } else {
            // Use original database value processing
            if (key === 'tablesAttribute' && hasRowsValues(attrValue)) {
              const values = attrValue.rowsValues
                .filter((row) => row.rowId)
                .map((row) => ({
                  id: row.rowId,
                  value:
                    row.email?.value ||
                    // This String conversion is necessary due to incorrect type definition
                    (row.number?.value !== undefined ? String(row.number.value) : '') ||
                    row.phoneNumber?.value ||
                    row.text?.value ||
                    row.reference?.value ||
                    '',
                }));
              if (values.length > 0) {
                result[attribute.formItem.id] = values;
              }
            } else if (key === 'dateAttribute' && 'date' in attrValue) {
              result[attribute.formItem.id] = [String(attrValue.date), attribute.formItem?.definition.date?.dateMask || 'Day'];
            } else if (key === 'computedAttribute' && 'value' in attrValue) {
              result[attribute.formItem.id] = formatComputedValue(attrValue['value'], i18n.language);
            } else {
              const subKeys = Object.keys(attrValue);

              if (subKeys.length > 0) {
                const subKey = subKeys[0] as keyof typeof attrValue;
                result[attribute.formItem.id] = attrValue[subKey];
              }
            }
          }
        }
      });
    });

    const joinedItem = item as FlattenedJoinedAgendaRecord;
    const joinedIdentity =
      'nestedRecord' in item || 'secondLevelNestedRecord' in item
        ? {
            nestedRecord: joinedItem.nestedRecord ?? null,
            secondLevelNestedRecord: joinedItem.secondLevelNestedRecord ?? null,
          }
        : {};

    return {
      ...result,
      id: item.id,
      identifier: joinedItem.rowId || item.id,
      agendaId: item.agendaId,
      ...joinedIdentity,
    } satisfies TransformedDataType;
  });

export const transformVisibilityResultsForTable = (
  data?: GetAgendaRecordsRes<AgendaRecordItem[]>
): Record<string, VisibilityResultWithName> | undefined => {
  if (!data?.items?.length) {
    return undefined;
  }

  return data.items.reduce<Record<string, VisibilityResultWithName>>((result, item) => {
    item.attributes.forEach((attribute) => {
      const name = attribute.formItem?.name;
      const formItemId = attribute.formItem?.id;
      if (formItemId) {
        const fieldPath = getFormFieldPath((item as FlattenedJoinedAgendaRecord).rowId || item.id, formItemId);
        result[fieldPath] = { ...attribute.visibilityResult };
        if (name) {
          result[fieldPath].formItemName = name;
        }
      }
    });

    return result;
  }, {});
};

export const getNestedListData = (nestedListForms: RegisteredForm[] | undefined) => {
  const data: NestedListData[] = [];

  if (!nestedListForms) return data;

  nestedListForms.forEach((form) => {
    const values = form.api.getValues?.() ?? {};
    const dataSchema = form.dataSchema || {};
    const dirtyFields = form.api.getDirtyFields();

    const { selectedRows = [], totalRows = [] } = form.data ?? {};
    const isPartialSelection = selectedRows.length > 0 && selectedRows.length < totalRows.length;

    const list = createNestedListData(values, dataSchema, dirtyFields, form.data ?? {}, isPartialSelection ? selectedRows : undefined);

    data.push(...list);
  });

  return data;
};

export const createNestedListData = (
  values: TableFormData,
  dataSchema: DataSchema,
  dirtyFields: TableDirtyFields,
  data: Omit<RegisteredFormData, 'formIdentifier'>,
  filterToRecordIds?: string[]
) => {
  const nestedListData: NestedListData[] = [];

  const createdRows = getCreatedRows(values, dataSchema);
  const updatedRows = getUpdatedRows(dirtyFields, values, dataSchema);
  const { agendaId = '', formItemId = '' } = data || {};

  const shouldIncludeRow = (rowId: string): boolean => {
    if (!filterToRecordIds) return true;
    return filterToRecordIds.includes(rowId);
  };

  Array.from(updatedRows.entries()).forEach(([rowId, attr]) => {
    if (shouldIncludeRow(rowId)) {
      nestedListData.push({
        agendaId,
        recordId: rowId,
        formItemId,
        attributes: attr,
      });
    }
  });
  Array.from(createdRows.entries()).forEach(([rowId, attr]) => {
    if (shouldIncludeRow(rowId)) {
      nestedListData.push({
        agendaId,
        recordId: null,
        formItemId,
        attributes: attr,
      });
    }
  });

  return nestedListData;
};

export const getCreatedRows = (data: Record<string, unknown> | undefined, dataSchema: DataSchema): RowsMap => {
  const formData = Object.entries(data || []);
  const createPerRowMap = new Map<string, AttributeType[]>();

  formData
    .filter(([rowId]) => rowId.startsWith(NEW_RECORD_PREFIX))
    .forEach(([rowId, columns]) => {
      const attributes = Object.entries(columns || [])
        .map(([itemId, value]) => {
          const type = dataSchema[itemId].formItemDefinition?.type;
          if (type) {
            return getAttributeForSave(type, itemId, value, dataSchema[itemId].formItemDefinition);
          }
        })
        .filter((attr): attr is AttributeType => attr !== undefined);

      createPerRowMap.set(rowId, attributes);
    });

  return createPerRowMap;
};

export const getAllUpdatedRows = (dirtyFields: TableDirtyFields, data: TableFormData | undefined, dataSchema: DataSchema): RowsMap => {
  const formData = data || {};

  const updatePerRowMap: RowsMap = new Map<string, AttributeType[]>();

  Object.entries(dirtyFields).forEach(([rowId]) => {
    const columns = formData[rowId] as unknown as AgendaAttributeValue[];

    const attributes = Object.entries(columns || [])
      .map(([itemId, value]) => {
        const type = dataSchema[itemId].formItemDefinition?.type;
        if (type) {
          return getAttributeForSave(type, itemId, value, dataSchema[itemId].formItemDefinition);
        }
      })
      .filter((attr): attr is AttributeType => attr !== undefined);

    updatePerRowMap.set(rowId, attributes);
  });

  return updatePerRowMap;
};

export const getUpdatedRows = (dirtyFields: TableDirtyFields, data: TableFormData | undefined, dataSchema: DataSchema): RowsMap => {
  const allUpdatedRowsMap = getAllUpdatedRows(dirtyFields, data, dataSchema);
  return new Map(Array.from(allUpdatedRowsMap).filter(([rowId]) => !rowId.startsWith(NEW_RECORD_PREFIX)));
};

// For calculation in rows
export const buildCalculateAttributes = (data: AttributeType[]) => {
  return Object.entries(data)

    .map(([_, value]) => {
      const { formItemId, ...rest } = value;

      return {
        formItemId,
        ...removeNulls(rest),
      };
    })
    .filter(Boolean);
};

const removeNulls = <T>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map(removeNulls) as T;
  } else if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)

        .filter(([_, v]) => v !== null)
        .map(([k, v]) => [k, removeNulls(v)])
    ) as T;
  }
  return obj;
};

export const mapCalculateResponseToFormValues = (attributes: CalculateResponseAttribute[]): Record<string, AgendaAttributeValue> => {
  return attributes.reduce<Record<string, AgendaAttributeValue>>((acc, attribute) => {
    const formItemId = attribute.formItem?.id;
    const value = setAttributeValue(attribute.formItem?.definition.type ?? '', attribute, false, attribute.formItem?.definition);
    if (formItemId) {
      acc[formItemId] = value;
    }
    return acc;
  }, {});
};

export const normalizeRecordId = (id: string): string => {
  return id.startsWith(NEW_RECORD_PREFIX) ? id.slice(NEW_RECORD_PREFIX.length) : id;
};

export const normalizeRecordIds = (ids: string[]) => {
  return ids.map(normalizeRecordId);
};

export const hydrateLightResponse = (light: RecordsForViewLightRes): GetAgendaRecordsRes<AgendaRecordItem[]> => {
  return {
    ...light.records,
    ...(light.displayValuesMetadata !== undefined ? { displayValuesMetadata: light.displayValuesMetadata } : {}),
    items: light.records.items.map((item) => ({
      ...item,
      attributes: item.attributes.map((attribute) => ({
        ...attribute,
        formItem: light.formItemDefinitions[attribute.formItemId] ?? null,
        state: null,
      })),
    })),
  };
};

export const getViewIdFromTabSections = (tabSections: TabSection[] | undefined, formItemId: string | undefined | null): string | undefined => {
  if (!tabSections || !formItemId) {
    return undefined;
  }

  for (const tabSection of tabSections) {
    for (const section of tabSection.sections) {
      if (!section.items) continue;

      const matchingItem = section.items.find((item: FormItem) => item.id === formItemId);

      if (matchingItem) {
        const { definition } = matchingItem;

        if (definition.type === 'Records' && definition.records) {
          return definition.records.viewId ?? '';
        }

        if (definition.type === 'Tables') {
          const tablesDefinition = definition.Tables || (definition as FormItem['definition'] & { tables?: TablesFormItem }).tables;
          if (tablesDefinition) {
            return tablesDefinition.viewId;
          }
        }
      }
    }
  }

  return undefined;
};

export const isPortalRecordsDefinition = (definition?: FormItemDefinition) =>
  definition?.type === 'Records' && definition.records?.viewMode === VIEW_MODE.PORTAL;

export const getPortalSafeValue = (definition: FormItemDefinition, value: AgendaAttributeValue) =>
  isPortalRecordsDefinition(definition) ? [] : value;

export const includeClickedButtonAttribute = (attributes: AttributeType[], clickedButtonId?: string) =>
  clickedButtonId && !attributes.some(({ formItemId }) => formItemId === clickedButtonId)
    ? [...attributes, getAttributeForSave('Button', clickedButtonId, true)]
    : attributes;
